/* eslint-disable tailwindcss/no-custom-classname */
import React, { FC, useEffect, useRef, useState } from 'react';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';
import { Tooltip } from '@mui/material';
import { MuiChipsInput } from 'mui-chips-input';
import { difference } from 'lodash';

import {
  ColorPicker,
  CustomModal,
  ImagePreviewCard,
  RhfDropdown,
  RhfTextField
} from '@components';
import {
  SHORT_FIELD_MAX_LENGTH,
  IMAGE_FORMATS,
  API_RESPONSE_STATUS,
  THIRTY_FIVE_CHAR_MAX_LENGTH
} from '@constants/common';
import { generateFileUrl } from '@utils/common';
import { translate } from '@utils/locale';
import { InfoIcon } from '@assets/icons';
import { colors } from '@constants/colors';
import { Sector } from '@type/util';
import { ToastTypes } from '@type/toast';
import { showGenericErrorToast } from '@utils/error';
import FormDualButton from '@components/form-dual-button/FormDualButton';
import displayToast from '@components/toast/customToast';
import FileUploader from '@containers/file-uploader/FileUploader';

import {
  Company,
  CompanyConfiguration,
  CreateCompanyFormFieldValues
} from '../types';
import { SectionMapper, StageValues } from '../constants';
import {
  useCreateCompanyEmailDomainMutation,
  useBulkDeleteCompanyEmailDomainsMutation,
  useLinkSectorMutation,
  useUnLinkSectorMutation,
  useUpdateCompanyConfigurationMutation,
  useUpdateCompanyMutation
} from '../api';
import { formatUpdateCompanyDealDataForApiCall } from '../services';
import SectorAndCategory from '../components/sector-and-category/SectorAndCategory';

const BrandSection: FC<{
  editMode?: SectionMapper;
  updateEditMode?: (section?: SectionMapper) => void;
  disableEdit?: boolean;
  companyId: string;
  companyDetails: Company;
  sectors: Sector[] | undefined;
  getResetFunction: (
    editMode?: SectionMapper
  ) => (formValues: FieldValues) => {};
  getResetBrandSection: (formValues: FieldValues) => {};
  companyConfiguration: CompanyConfiguration | undefined;
}> = ({
  editMode,
  updateEditMode,
  disableEdit,
  companyId,
  companyDetails,
  getResetFunction,
  getResetBrandSection,
  sectors,
  companyConfiguration
}) => {
  const {
    control,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
    trigger,
    reset
  } = useFormContext();

  const companyBrandNameRef = useRef<HTMLInputElement | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [deleteClickedFile, setDeleteClickedFile] = useState<string>('');
  const [isConfirmModalOpen, setConfirmModalOpen] = useState<
    'discard' | 'save' | undefined
  >();

  const [createCompanyEmailDomain] = useCreateCompanyEmailDomainMutation();
  const [bulkDeleteCompanyEmailDomains] =
    useBulkDeleteCompanyEmailDomainsMutation();
  const [updateCompany] = useUpdateCompanyMutation();
  const [isCancelClicked, setIsCancelClicked] = useState<boolean>(true);

  const [linkSectorToCompany] = useLinkSectorMutation();
  const [unlinkSectorToCompany] = useUnLinkSectorMutation();
  const [updateCompanyConfiguration] = useUpdateCompanyConfigurationMutation();

  const isEditMode = editMode === SectionMapper.brand;

  const companyStageValues = Object.keys(StageValues).map(key => ({
    id: key,
    name: StageValues[key as keyof typeof StageValues]
  }));

  const onCompanyBrandNameTextFieldClick = () =>
    clearErrors('companyBrandName');

  const onWebsiteTextFieldClick = () => clearErrors('website');

  const deleteFile = (value: string) => {
    setDeleteClickedFile(value);
    setValue(value as never as keyof CreateCompanyFormFieldValues, {
      file: undefined,
      path: ''
    });
  };

  const onCompanyOneLinerTextFieldClick = () => clearErrors('companyOneLiner');

  const onDetailedDescriptionTextFieldClick = () =>
    clearErrors('detailedDescription');

  const onColorPickerSelect = (color: string) => {
    clearErrors('hexValue');
    setValue('hexValue', color.toUpperCase(), { shouldDirty: true });
  };

  const onEditSaveButtonClick = async () => {
    if (!editMode) {
      updateEditMode?.(SectionMapper.brand);
      return;
    }
    if (editMode !== SectionMapper.brand) {
      setConfirmModalOpen('discard');
      return;
    }
    const isFormValid = await trigger([
      'companyBrandName',
      'website',
      'companyLogo',
      'companyStage',
      'sector',
      'companyOneLiner',
      'detailedDescription',
      'companyEmailDomains',
      'hexValue'
    ]);

    if (isFormValid) {
      setConfirmModalOpen('save');
    }
  };

  const onUpdateDetails = async () => {
    setIsLoading(true);

    const values = getValues();
    try {
      //Hexcode update
      const hex_value = values.hexValue
        ? `0xFF${values.hexValue.substring(1)}`
        : undefined;
      if (hex_value) {
        const res = await updateCompanyConfiguration({
          companyId: companyId,
          configurationId: companyConfiguration?.id ?? '',
          payload: {
            brand_color: hex_value
          }
        }).unwrap();
        if (res.error) {
          displayToast(
            ToastTypes.ERROR,
            res.error?.details?.[0] ?? 'Failed to update hexcode value'
          );
          setIsLoading(false);
          return;
        }
      }

      // Email domain update
      const editedEmailDomainValues = values.companyEmailDomains;
      const existingEmailDomainValues =
        companyDetails?.email_domains
          ?.map(item => item.domain_name)
          .filter(item => !!item) ?? [];

      const newlyAddedEmailDomains = difference(
        editedEmailDomainValues,
        existingEmailDomainValues
      );
      const removedEmailDomains = difference(
        existingEmailDomainValues,
        editedEmailDomainValues
      );
      if (newlyAddedEmailDomains.length) {
        for (const name of newlyAddedEmailDomains) {
          const res = await createCompanyEmailDomain({
            company_id: companyId,
            payload: {
              domain_name: name
            }
          }).unwrap();
          if (res.error) {
            displayToast(
              ToastTypes.ERROR,
              res.error?.details?.[0] ??
                'Failed to add email domain to company '
            );
            setIsLoading(false);
            return;
          }
        }
      }
      if (removedEmailDomains.length) {
        let deleteEmailIds =
          companyDetails?.email_domains?.reduce((acc, item) => {
            if (removedEmailDomains.includes(item.domain_name)) {
              return [...acc, item.id];
            }
            return acc;
          }, []) ?? [];

        const res = await bulkDeleteCompanyEmailDomains({
          company_id: companyId,
          email_domain_ids: deleteEmailIds
        }).unwrap();
        if (res.error) {
          displayToast(
            ToastTypes.ERROR,
            res.error?.details?.[0] ?? 'Failed to remove email domain'
          );
          setIsLoading(false);
          return;
        }
      }

      // Sector update
      const editedSectors = values.sector.split(', ');
      const existingSectors =
        companyDetails?.sector_and_category
          ?.map(item => item.name)
          .filter(item => !!item) ?? [];

      const newlyAddedSectors = difference(editedSectors, existingSectors);
      const removedSectors = difference(existingSectors, editedSectors);
      if (newlyAddedSectors.length) {
        let sectorIds =
          sectors?.reduce((acc, item) => {
            if (newlyAddedSectors.includes(item.name)) {
              return [...acc, item.id];
            }
            return acc;
          }, []) ?? [];

        const res = await linkSectorToCompany({
          company_id: companyId,
          sector_ids: sectorIds
        }).unwrap();
        if (res.error) {
          setIsLoading(false);
          displayToast(
            ToastTypes.ERROR,
            res.error?.details?.[0] ?? 'Failed to link sector to company '
          );
        }
      }
      if (removedSectors.length) {
        let sectorIds =
          sectors?.reduce((acc, item) => {
            if (removedSectors.includes(item.name)) {
              return [...acc, item.id];
            }
            return acc;
          }, []) ?? [];

        const res = await unlinkSectorToCompany({
          company_id: companyId,
          sector_ids: sectorIds
        }).unwrap();
        if (res?.error) {
          setIsLoading(false);
          displayToast(
            ToastTypes.ERROR,
            res.error?.details?.[0] ?? 'Failed to link sector to company '
          );
        }
      }

      // Company Update
      const response = await updateCompany(
        formatUpdateCompanyDealDataForApiCall(companyId, companyDetails, {
          company_known_name: values.companyBrandName?.trim(),
          website: values.website,
          company_one_liner: values.companyOneLiner?.trim(),
          company_description: values.detailedDescription,
          hex_value: hex_value,
          icon_url: values.companyLogo?.path,
          company_stage: values.companyStage
        })
      ).unwrap();
      if (response && response.status === API_RESPONSE_STATUS.OK) {
        displayToast(ToastTypes.SUCCESS, 'Brand Updated Successfully');
        updateEditMode?.();
        setConfirmModalOpen(undefined);
      } else
        displayToast(
          ToastTypes.ERROR,
          response.error?.details?.[0] ?? 'Failed to Update Brand'
        );
    } catch (err) {
      setIsLoading(false);
      showGenericErrorToast(err);
    }
    setIsLoading(false);
  };

  const onDiscardDetails = () => {
    const values = getValues();
    reset(getResetFunction(editMode)(values));
    isCancelClicked
      ? updateEditMode?.()
      : updateEditMode?.(SectionMapper.brand);
    setConfirmModalOpen(undefined);
  };

  const onClickCancelButton = () => {
    setIsCancelClicked(true);
    setConfirmModalOpen('discard');
  };

  useEffect(() => {
    companyBrandNameRef.current?.focus();
  }, [editMode]);

  useEffect(() => {
    reset(getResetBrandSection(getValues()));
  }, [companyDetails]);

  return (
    <>
      <div className="flex flex-row justify-between items-center">
        <div className="text-xl font-semibold">
          {translate('company.brandSection.title')}
        </div>
        <FormDualButton
          enableEdit={!disableEdit}
          showCancelOrSaveButton={isEditMode}
          onClickCancelButton={onClickCancelButton}
          onClickEditButton={onEditSaveButtonClick}
        />
      </div>
      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-[70%]">
          <div className="flex flex-row mr-6">
            <RhfTextField
              name="companyBrandName"
              label={translate('company.brandSection.companyBrandName')}
              placeholder={translate('company.placeholders.companyBrandName')}
              control={control}
              errors={errors}
              wrapperClass="pt-4 mr-6 w-full"
              onClickTextField={onCompanyBrandNameTextFieldClick}
              maxLength={SHORT_FIELD_MAX_LENGTH}
              description={translate('company.description.companyBrandName')}
              isDisabled={!isEditMode}
              fieldRef={companyBrandNameRef}
              showCharacterLimit
            />
            <RhfTextField
              name="website"
              label={translate('company.brandSection.website')}
              placeholder={translate('company.placeholders.websiteUrl')}
              control={control}
              errors={errors}
              wrapperClass="pt-4 w-full"
              onClickTextField={onWebsiteTextFieldClick}
              description={translate('company.description.website')}
              type="url"
              isDisabled={!isEditMode}
              showCharacterLimit
            />
          </div>
          <div className="flex flex-row mr-6">
            <RhfDropdown
              control={control}
              name="companyStage"
              dropdownLOV={companyStageValues}
              errors={errors}
              descriptor={translate('company.placeholders.companyStage')}
              selectedItem={getValues('companyStage')}
              placeholder={translate('company.brandSection.companyStage')}
              wrapperClass="pt-4 mr-6 w-full"
              isDisabled={!isEditMode}
              onChangeHandler={() => clearErrors('companyStage')}
              description={translate('company.description.companyStage')}
            />

            {/* Sector and Category */}
            <SectorAndCategory
              isEditMode={editMode === SectionMapper.brand}
              errors={errors}
              sectors={sectors ?? []}
              description={translate('company.description.sector')}
            />
          </div>
          <div className="flex flex-row mr-6">
            <RhfTextField
              name="companyOneLiner"
              label={translate('company.brandSection.companyOneLiner')}
              placeholder={translate('company.placeholders.companyOneliner')}
              control={control}
              errors={errors}
              wrapperClass="pt-6 w-full"
              onClickTextField={onCompanyOneLinerTextFieldClick}
              maxLength={THIRTY_FIVE_CHAR_MAX_LENGTH}
              showCharacterLimit
              isDisabled={!isEditMode}
              description={translate('company.description.companyOneLiner')}
            />
          </div>
          <div className="flex flex-row mr-6">
            <RhfTextField
              name="detailedDescription"
              label={translate('company.brandSection.detailedDescription')}
              placeholder={translate(
                'company.placeholders.companyDetailedDescription'
              )}
              control={control}
              errors={errors}
              wrapperClass="pt-6 w-full"
              onClickTextField={onDetailedDescriptionTextFieldClick}
              maxLength={SHORT_FIELD_MAX_LENGTH}
              multiline
              rows={3}
              isDisabled={!isEditMode}
              description={translate('company.description.detailedDescription')}
              showCharacterLimit
            />
          </div>
          <div className="flex flex-row mr-6">
            <Controller
              name="companyEmailDomains"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div className="pt-6 mr-6 w-full">
                    <div>
                      <MuiChipsInput
                        label={translate(
                          'company.brandSection.companyEmailDomains'
                        )}
                        placeholder={
                          field.value?.length
                            ? ''
                            : translate(
                                'company.placeholders.companyEmailDomains'
                              )
                        }
                        className="w-full"
                        {...field}
                        error={fieldState.invalid}
                        disabled={!isEditMode}
                        addOnBlur
                      />
                    </div>
                    <p
                      className={`mx-[14px] mt-[3px] text-xs leading-5 text-black ${
                        isEditMode ? 'text-opacity-60' : 'text-opacity-40'
                      }`}>
                      {translate('company.description.companyEmailDomains')}
                    </p>
                  </div>
                );
              }}
            />
            <Controller
              name="hexValue"
              control={control}
              render={() => (
                <div className="mt-6 w-full">
                  {isEditMode ? (
                    <ColorPicker
                      defaultColor={getValues('hexValue') || '#000000'}
                      handleOnSelect={onColorPickerSelect}
                      parentElement={
                        <>
                          <div className="flex gap-2 w-full">
                            <RhfTextField
                              name="hexValue"
                              control={control}
                              errors={errors}
                              wrapperClass="w-full"
                              label={translate('company.brandSection.hexValue')}
                            />
                            <div
                              style={{
                                background: `${
                                  getValues('hexValue')
                                    ? getValues('hexValue')
                                    : '#000000'
                                }`
                              }}
                              className={'w-14 h-14 cursor-pointer'}></div>
                          </div>
                        </>
                      }
                      // errorMessage={String(errors?.hexValue?.message ?? '')}
                    />
                  ) : (
                    <>
                      {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
                      <div className="flex gap-2 w-full">
                        <div
                          className={`flex grow items-center p-3 h-14 text-[#000000ab] rounded-[5px] border cursor-pointer border-gray88 ${
                            errors?.hexValue?.message
                              ? 'border-red '
                              : 'border-gray88'
                          }`}>
                          {getValues('hexValue')
                            ? `${getValues('hexValue')}`
                            : translate('company.brandSection.hexValue')}
                        </div>
                        <div
                          style={{
                            background: `${getValues('hexValue')}`
                          }}
                          className={'w-14 h-14 cursor-pointer'}></div>
                      </div>
                      {errors?.hexValue?.message ? (
                        <p className="mx-[14px] mt-[3px] text-xs leading-5 text-red">
                          {errors?.hexValue?.message}
                        </p>
                      ) : (
                        // eslint-disable-next-line tailwindcss/no-custom-classname
                        <p
                          className={`mx-[14px] mt-[3px] text-xs leading-5 text-black ${
                            isEditMode ? 'text-opacity-60' : 'text-opacity-40'
                          }`}>
                          {translate('company.description.hexValue')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        {/* Logo section */}
        <div className="mt-3">
          <div className="flex flex-row items-center pb-2 text-base font-normal text-black/60">
            {translate('deals.companyLogo')}
            <Tooltip
              title={translate('deals.companyLogoInfoText')}
              arrow={true}
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px'
                  }
                }
              }}>
              <InfoIcon
                height={16}
                width={16}
                className="ml-1 cursor-pointer"
                fill={colors.black60}
              />
            </Tooltip>
          </div>
          {deleteClickedFile === 'companyLogo' ||
          !getValues('companyLogo.path') ? (
            <FileUploader
              name="companyLogo"
              enableCrop={true}
              sizeInMb={3}
              supportedFormats={IMAGE_FORMATS}
              fileUploadSuccessHandler={(file, path) => {
                setValue('companyLogo', { file: file, path: path });
                clearErrors('companyLogo');
                setDeleteClickedFile('');
              }}
              deleteFileHandler={() =>
                setValue('companyLogo', { file: undefined, path: '' })
              }
              errors={errors}
              previousFile={getValues('companyLogo')?.file}
              aspectRatio={1}
              imageInfo={translate('deals.companyLogoInfoText')}
              showPreview={true}
              isDisabled={!isEditMode}
              customUploadText={translate('company.placeholders.companyLogo')}
              description={translate('company.description.companyLogo')}
            />
          ) : (
            <ImagePreviewCard
              name="companyLogo"
              isEdit={isEditMode}
              handleDeleteClick={() => deleteFile('companyLogo')}
              imageUrl={generateFileUrl(
                getValues('companyLogo.path'),
                getValues('companyLogo.file')
              )}
              fileName={getValues('companyLogo.file')?.name || ''}
              description={translate('company.description.companyLogo')}
            />
          )}
        </div>
      </div>
      {isConfirmModalOpen && (
        <CustomModal
          isModalOpen={!!isConfirmModalOpen}
          header={
            isConfirmModalOpen === 'discard'
              ? translate('modals.discardPopupTitle')
              : translate('modals.saveInfoTitle')
          }
          primaryButtonLabel={
            isConfirmModalOpen === 'discard'
              ? translate('modals.confirm')
              : translate('update')
          }
          secondaryButtonVariant="text"
          secondaryButtonLabel={translate('modals.cancelButtonTitle')}
          onChangePrimaryButton={
            isConfirmModalOpen === 'discard'
              ? onDiscardDetails
              : onUpdateDetails
          }
          onChangeSecondaryButton={() => setConfirmModalOpen(undefined)}
          isPrimaryButtonLoading={isLoading}
        />
      )}
    </>
  );
};

export default BrandSection;
